import React, { useEffect, useRef, useState } from "react";
import { Card, ButtonComponent, InputComponent } from "../../../components/ui";
import AlertNotification from "../../../components/notification";
import {
  getSalesqlSettings,
  saveSalesqlToken,
  disconnectSalesqlAccount,
  startSalesqlSync,
  cancelSalesqlSync,
  getSalesqlSyncStatus,
} from "../../../controllers/salesqlController";

const POLL_INTERVAL_MS = 3000;

const formatDuration = (ms) => {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return "-";
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  return `${minutes}m ${seconds}s`;
};

function SalesQLSync() {
  const [settings, setSettings] = useState({ connected: false, lastSyncAt: null });
  const [tokenInput, setTokenInput] = useState("");
  const [savingToken, setSavingToken] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [notification, setNotification] = useState(null);
  const [status, setStatus] = useState(false);

  const [syncStatus, setSyncStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [starting, setStarting] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const pollTimerRef = useRef(null);
  const logEndRef = useRef(null);
  const hasInitializedRef = useRef(false);

  const loadSettings = async () => {
    try {
      const data = await getSalesqlSettings();
      setSettings(data);
    } catch (error) {
      console.error("Failed to load SalesQL settings", error);
    }
  };

  useEffect(() => {
    // StrictMode double-invokes effects in development, which would otherwise start
    // two independent polling loops (each appending the same fetched log lines,
    // duplicating every log entry in the UI). Guard so the real kickoff runs once.
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    loadSettings();

    // Resume live polling if a sync is already running (e.g. the admin refreshed mid-sync).
    const checkForRunningSync = async () => {
      try {
        const data = await getSalesqlSyncStatus();
        if (data.status === "running") {
          setSyncStatus(data);
          setLogs(data.logs || []);
          const lastLogId = data.logs?.length ? data.logs[data.logs.length - 1].id : 0;
          pollStatus(data.syncRunId, lastLogId);
        }
      } catch (error) {
        console.error("Failed to check for a running sync", error);
      }
    };
    checkForRunningSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logs]);

  const pollStatus = async (runId, lastLogId) => {
    try {
      const data = await getSalesqlSyncStatus(runId, lastLogId);
      setSyncStatus(data);
      if (data.logs?.length) {
        setLogs((prev) => [...prev, ...data.logs]);
      }

      if (data.status === "running") {
        const nextLastLogId = data.logs?.length
          ? data.logs[data.logs.length - 1].id
          : lastLogId;
        pollTimerRef.current = setTimeout(() => pollStatus(runId, nextLastLogId), POLL_INTERVAL_MS);
      } else {
        loadSettings();
      }
    } catch (error) {
      console.error("Failed to fetch sync status", error);
      pollTimerRef.current = setTimeout(() => pollStatus(runId, lastLogId), POLL_INTERVAL_MS);
    }
  };

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  const handleSaveToken = async () => {
    if (!tokenInput.trim()) return;
    setSavingToken(true);
    try {
      await saveSalesqlToken(tokenInput.trim());
      setTokenInput("");
      setNotification("SalesQL token saved successfully.");
      setStatus(true);
      loadSettings();
    } catch (error) {
      setNotification(error?.response?.data?.error || "Failed to save SalesQL token.");
      setStatus(false);
    } finally {
      setSavingToken(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await disconnectSalesqlAccount();
      setNotification("SalesQL account disconnected.");
      setStatus(true);
      loadSettings();
    } catch (error) {
      setNotification(error?.response?.data?.error || "Failed to disconnect SalesQL account.");
      setStatus(false);
    } finally {
      setDisconnecting(false);
    }
  };

  const handleStartSync = async () => {
    setStarting(true);
    setLogs([]);
    setSyncStatus(null);
    try {
      const { syncRunId: newRunId } = await startSalesqlSync();
      pollStatus(newRunId, 0);
    } catch (error) {
      setNotification(error?.response?.data?.error || "Failed to start synchronization.");
      setStatus(false);
    } finally {
      setStarting(false);
    }
  };

  const handleCancelSync = async () => {
    setCancelling(true);
    try {
      await cancelSalesqlSync();
      setNotification("Cancellation requested — the sync will stop shortly.");
      setStatus(true);
    } catch (error) {
      setNotification(error?.response?.data?.error || "Failed to cancel synchronization.");
      setStatus(false);
    } finally {
      setCancelling(false);
    }
  };

  const isRunning = syncStatus?.status === "running";
  const progressPercent =
    syncStatus?.totalFiles > 0
      ? Math.min(100, Math.round((syncStatus.processedFiles / syncStatus.totalFiles) * 100))
      : 0;

  return (
    <div className="p-5 w-full max-w-3xl mx-auto space-y-5">
      <Card>
        <h3 className="text-lg font-semibold mb-3">Connection</h3>
        <div className="flex items-center gap-3 mb-4">
          <span
            className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${
              settings.connected
                ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-300"
                : "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-500/15 dark:text-amber-300"
            }`}
          >
            {settings.connected ? "Connected" : "Not connected"}
          </span>
          <span className="text-sm text-gray-500 dark:text-slate-400">
            Last synchronized:{" "}
            {settings.lastSyncAt ? new Date(settings.lastSyncAt).toLocaleString() : "Never"}
          </span>
          {settings.connected && (
            <ButtonComponent
              onClick={handleDisconnect}
              disabled={disconnecting || isRunning}
              className="bg-red-600 hover:bg-red-600 focus:ring-red-300 dark:bg-red-600 dark:hover:bg-red-600 dark:focus:ring-red-800 ml-auto"
            >
              {disconnecting ? "Disconnecting..." : "Disconnect"}
            </ButtonComponent>
          )}
        </div>

        <div className="flex gap-3">
          <InputComponent
            type="password"
            placeholder={
              settings.connected
                ? "Disconnect first, then paste a new token to switch accounts"
                : "Enter SalesQL API token"
            }
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            disabled={settings.connected}
            className="flex-1"
          />
          <ButtonComponent
            onClick={handleSaveToken}
            disabled={savingToken || !tokenInput.trim() || settings.connected}
          >
            {savingToken ? "Saving..." : "Connect"}
          </ButtonComponent>
        </div>
        <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">
          Paste the token exactly as shown in your SalesQL account (with or without the
          &ldquo;Token &rdquo; prefix — either works).
        </p>
      </Card>

      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">Synchronize</h3>
          <div className="flex gap-2">
            {isRunning && (
              <ButtonComponent
                onClick={handleCancelSync}
                disabled={cancelling || syncStatus?.cancelRequested}
                className="bg-red-600 hover:bg-red-600 focus:ring-red-300 dark:bg-red-600 dark:hover:bg-red-600 dark:focus:ring-red-800"
              >
                {syncStatus?.cancelRequested ? "Cancelling..." : "Cancel"}
              </ButtonComponent>
            )}
            <ButtonComponent
              onClick={handleStartSync}
              disabled={!settings.connected || starting || isRunning}
              className="bg-[#2470B7] hover:bg-[#2470B7] dark:bg-[#2470B7] dark:hover:bg-[#2470B7]"
            >
              {isRunning ? "Synchronizing..." : "Synchronize"}
            </ButtonComponent>
          </div>
        </div>

        {syncStatus && (
          <div className="mb-4 space-y-2">
            <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-slate-700">
              <div
                className="bg-[#2470B7] h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600 dark:text-slate-300">
              <span>
                Files: {syncStatus.processedFiles}/{syncStatus.totalFiles}
              </span>
              <span>Imported: {syncStatus.importedCount}</span>
              <span>Skipped (duplicates): {syncStatus.skippedCount}</span>
              {isRunning && <span>ETA: {formatDuration(syncStatus.etaMs)}</span>}
              <span className="font-semibold capitalize">{syncStatus.status}</span>
            </div>
          </div>
        )}

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 h-72 overflow-y-auto font-mono text-xs text-gray-700 dark:border-slate-700 dark:bg-slate-950/60 dark:text-slate-300">
          {logs.length === 0 ? (
            <p className="text-gray-400 dark:text-slate-400">No activity yet.</p>
          ) : (
            logs.map((log) => (
              <div key={log.id}>
                <span className="text-gray-400 dark:text-slate-400">
                  [{new Date(log.createdAt).toLocaleTimeString()}]
                </span>{" "}
                {log.message}
              </div>
            ))
          )}
          <div ref={logEndRef} />
        </div>
      </Card>

      {notification && (
        <AlertNotification text={notification} status={status} onClose={() => setNotification(null)} />
      )}
    </div>
  );
}

export default SalesQLSync;
